/**
 * ReservationMethodChooser
 *
 * Modal shown to agencies (role="acente") when they click the primary
 * "Rezervasyon Oluştur" CTA. Presents two big, tactile choices:
 *
 *   1. Manuel Giriş — 4-adımlı formu doğrudan aç.
 *   2. Fotoğraf / Belge ile AI — formu aç ve fotoğraf yükleyiciyi
 *      hemen tetikle (form URL'ine ?mode=ai eklenerek yönlendirilir;
 *      AcenteReservationForm ilk render'da input'u tıklar).
 *
 * The modal is intentionally big & warm to reduce agency cognitive load —
 * this is the primary daily task, not a menu buried in the sidebar.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PenLine, Sparkles, Camera, ArrowRight } from "lucide-react";

export default function ReservationMethodChooser({ open, onOpenChange }) {
  const nav = useNavigate();

  const pick = (mode) => {
    onOpenChange(false);
    // Send explicit ?mode= so the form knows which UX to render — manual
    // hides the AI photo card, ai auto-opens the file picker.
    nav(`/acente/rezervasyon/yeni?mode=${mode}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw] p-0 overflow-hidden bg-white border-0"
        data-testid="reservation-method-chooser"
      >
        {/* Hero band */}
        <div className="bg-gradient-to-br from-brand-navy via-brand-navy to-brand-orange-dark text-white p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-brand-gold/25 blur-3xl" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-gold font-semibold">
              Yeni Rezervasyon
            </div>
            <DialogHeader className="text-left">
              <DialogTitle className="font-serif text-2xl sm:text-3xl text-white mt-1">
                Nasıl başlayalım?
              </DialogTitle>
              <DialogDescription className="text-white/90 text-sm mt-2">
                İki hızlı yol var — elle yazıp adım adım ilerleyin ya da elinizdeki
                fotoğrafı / ekran görüntüsünü yükleyip yapay zekanın doldurmasına izin verin.
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Choice cards */}
        <div className="p-6 sm:p-8 grid gap-4 sm:grid-cols-2">
          <button
            onClick={() => pick("manual")}
            className="group text-left rounded-2xl border-2 border-brand-navy/10 hover:border-brand-navy bg-white p-5 transition-all hover:shadow-lg hover:-translate-y-0.5"
            data-testid="method-manual"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-navy to-brand-navy/80 text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              <PenLine size={26} />
            </div>
            <div className="mt-4">
              <div className="font-serif text-xl text-brand-navy">Manuel Giriş Yap</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                4 kısa adımda otel, tarihler, kişi ve oda bilgilerini kendiniz girin.
                Adım başlıklarına tıklayıp istediğiniz yere dönebilirsiniz.
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-navy group-hover:gap-2 transition-all">
                Devam <ArrowRight size={12} />
              </div>
            </div>
          </button>

          <button
            onClick={() => pick("ai")}
            className="group text-left rounded-2xl border-2 border-brand-orange/30 hover:border-brand-orange bg-gradient-to-br from-brand-orange/5 via-white to-brand-gold/10 p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 relative overflow-hidden"
            data-testid="method-ai"
          >
            <div className="absolute -top-3 -right-3 bg-brand-orange text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full shadow-md">
              Hızlı
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-orange to-brand-orange-dark text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              <Sparkles size={26} />
            </div>
            <div className="mt-4">
              <div className="font-serif text-xl text-brand-navy flex items-center gap-2">
                Fotoğraf ile AI
              </div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Elinizdeki not, ekran görüntüsü ya da rezervasyon formunun fotoğrafını yükleyin —
                yapay zeka otel, tarih, kişi ve odaları otomatik doldursun.
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange group-hover:gap-2 transition-all">
                <Camera size={12} /> Fotoğraf Yükle <ArrowRight size={12} />
              </div>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
